import { useState, useCallback } from "react";
import type { GenerateRationaleResponse } from "@/types/ab-tests";
import type { VariantBSnapshot, ScorecardGapSnapshot } from "@/components/ab-tests/create-test-modal.types";

interface UseUndoRedoParams {
  variantBTitle: string;
  variantBBullets: Record<string, string>;
  appliedScorecardGaps: string[];
  lastSource: string | null;
  lastSourceDetail: string;
  rationaleData: GenerateRationaleResponse | null;
  gapSnapshots: Record<string, ScorecardGapSnapshot>;
  /** AUT-430 (WEB-008): FR-15 -- the strategy link and target phrase ride the same stack. */
  strategyLinkId: number | null | undefined;
  targetPhrase: string;
  setVariantBTitle: (v: string) => void;
  setVariantBBullets: (v: Record<string, string>) => void;
  setAppliedScorecardGaps: (v: string[]) => void;
  setLastSource: (v: string | null) => void;
  setLastSourceDetail: (v: string) => void;
  setRationaleData: (v: GenerateRationaleResponse | null) => void;
  setGapSnapshots: (v: Record<string, ScorecardGapSnapshot>) => void;
  setStrategyLinkId: (v: number | null | undefined) => void;
  setTargetPhrase: (v: string) => void;
}

export function useUndoRedo({
  variantBTitle,
  variantBBullets,
  appliedScorecardGaps,
  lastSource,
  lastSourceDetail,
  rationaleData,
  gapSnapshots,
  strategyLinkId,
  targetPhrase,
  setVariantBTitle,
  setVariantBBullets,
  setAppliedScorecardGaps,
  setLastSource,
  setLastSourceDetail,
  setRationaleData,
  setGapSnapshots,
  setStrategyLinkId,
  setTargetPhrase,
}: UseUndoRedoParams) {
  const [undoStack, setUndoStack] = useState<VariantBSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<VariantBSnapshot[]>([]);

  const snapshotVariantB = useCallback((): VariantBSnapshot => ({
    title: variantBTitle,
    bullets: { ...variantBBullets },
    appliedScorecardGaps: [...appliedScorecardGaps],
    lastSource,
    lastSourceDetail,
    rationaleData,
    gapSnapshots: { ...gapSnapshots },
    strategyLinkId,
    targetPhrase,
  }), [variantBTitle, variantBBullets, appliedScorecardGaps, lastSource, lastSourceDetail, rationaleData, gapSnapshots, strategyLinkId, targetPhrase]);

  const restoreSnapshot = useCallback((snapshot: VariantBSnapshot) => {
    setVariantBTitle(snapshot.title);
    setVariantBBullets(snapshot.bullets);
    setAppliedScorecardGaps(snapshot.appliedScorecardGaps);
    setLastSource(snapshot.lastSource);
    setLastSourceDetail(snapshot.lastSourceDetail);
    setRationaleData(snapshot.rationaleData);
    setGapSnapshots(snapshot.gapSnapshots);
    setStrategyLinkId(snapshot.strategyLinkId);
    setTargetPhrase(snapshot.targetPhrase);
  }, [setVariantBTitle, setVariantBBullets, setAppliedScorecardGaps, setLastSource, setLastSourceDetail, setRationaleData, setGapSnapshots, setStrategyLinkId, setTargetPhrase]);

  const pushHistory = useCallback(() => {
    setUndoStack(prev => [...prev, snapshotVariantB()]);
    setRedoStack([]);
  }, [snapshotVariantB]);

  const handleUndo = useCallback(() => {
    setUndoStack(prev => {
      if (!prev.length) return prev;
      const next = [...prev];
      const last = next.pop()!;
      setRedoStack(r => [...r, snapshotVariantB()]);
      restoreSnapshot(last);
      return next;
    });
  }, [snapshotVariantB, restoreSnapshot]);

  const handleRedo = useCallback(() => {
    setRedoStack(prev => {
      if (!prev.length) return prev;
      const next = [...prev];
      const last = next.pop()!;
      setUndoStack(u => [...u, snapshotVariantB()]);
      restoreSnapshot(last);
      return next;
    });
  }, [snapshotVariantB, restoreSnapshot]);

  const resetStacks = useCallback(() => {
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  return {
    undoStack,
    redoStack,
    pushHistory,
    handleUndo,
    handleRedo,
    resetStacks,
    setUndoStack,
    setRedoStack,
  };
}
